import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FRANCHISE_IMGS } from "../constants";
import { useLang } from "../i18n";
import { useQuote } from "../quote";

const fadeUp = {
  hidden: { opacity: 0, y: 44 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Franchise() {
  const { t } = useLang();
  const { openDrawer } = useQuote();
  const caps = t("franchise.caps");

  const tiles = [
    { src: FRANCHISE_IMGS.facade, cap: caps[0], cls: "lg:col-span-4 h-[420px] md:h-[560px]" },
    { src: FRANCHISE_IMGS.bar, cap: caps[1], cls: "lg:col-span-8 h-[420px] md:h-[560px]" },
    { src: FRANCHISE_IMGS.bistrot, cap: caps[2], cls: "lg:col-span-7 h-[300px] md:h-[380px]" },
    { src: FRANCHISE_IMGS.kiosk, cap: caps[3], cls: "lg:col-span-5 h-[300px] md:h-[380px]" },
    { src: FRANCHISE_IMGS.lounge, cap: caps[4], cls: "lg:col-span-5 h-[300px] md:h-[380px]" },
    { src: FRANCHISE_IMGS.terrace, cap: caps[5], cls: "lg:col-span-7 h-[300px] md:h-[380px]" },
  ];

  return (
    <section
      id="franchising"
      data-testid="franchising"
      className="relative px-6 py-28 md:px-12 md:py-40"
      style={{ backgroundImage: "url(/parchment.jpg)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="pointer-events-none absolute inset-0 bg-[#F7EDD3]/60" />
      <div className="relative mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-[0.65rem] uppercase tracking-[0.5em] text-goldink"
          >
            {t("franchise.eyebrow")}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display text-ink mt-6 text-4xl leading-tight md:text-6xl"
          >
            {t("franchise.title")}
          </motion.h2>
        </div>
        <motion.p
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="max-w-md text-lg leading-relaxed text-ink/85"
        >
          {t("franchise.body")}
        </motion.p>
      </div>

      <motion.div
        variants={{ show: { transition: { staggerChildren: 0.08 } } }}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.08 }}
        className="relative grid gap-6 lg:grid-cols-12"
      >
        {tiles.map((tile, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            custom={i}
            data-testid={`franchise-tile-${i}`}
            className={`clip-frame-sm group relative overflow-hidden border border-gold/20 ${tile.cls}`}
          >
            <img
              src={tile.src}
              alt={tile.cap}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-transparent to-transparent" />
            <span className="absolute bottom-5 start-6 text-[0.6rem] uppercase tracking-[0.35em] text-gold">{tile.cap}</span>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        variants={fadeUp}
        custom={2}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="mt-14 flex justify-center"
      >
        <button onClick={() => openDrawer("franchise")} data-testid="franchise-cta-button" className="btn-gold">
          {t("franchise.cta")} <ArrowRight size={15} />
        </button>
      </motion.div>
    </section>
  );
}
